import { HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr'
import type { HubConnection } from '@microsoft/signalr'
import { apiBaseUrl } from '../../api/client'
import { getAccessToken } from '../../auth/tokenStore'

/**
 * One hub connection for the whole app.
 *
 * **One**, reference-counted, rather than one per screen: the shell subscribes to announcements
 * for as long as someone is signed in, and a session screen subscribes on top of that. Two
 * connections would mean two handshakes, two reconnect timers, and — under HTTP/1.1 — two of the
 * browser's six per-origin slots.
 *
 * `@microsoft/signalr` is the sixth runtime dependency this app takes, after five, and that is
 * worth saying out loud in a project that hand-wrote its own service worker rather than take
 * Workbox. It earns itself on four things a hand-rolled event stream would each have to
 * reimplement: groups, reconnection with backoff, transport fallback, and presence
 * (`docs/web-api/phase-9-kickoff.md` §1, decision 2).
 *
 * **The token travels in the query string**, because a browser cannot set a header on a WebSocket
 * handshake. That reverses the reasoning `api/sse.ts` was written on, and it is why the API
 * accepts a query-string bearer for `/hubs` and nowhere else. It is recorded as a risk in §6.
 */

let connection: HubConnection | null = null
let refs = 0

/** Where the hub lives, derived from the API base so a split-origin deployment works unchanged. */
function hubUrl(): string {
  const base = apiBaseUrl()

  // The API base is `…/api/v1`; the hub is a sibling of `/api`, not a child of it.
  try {
    return new URL('/hubs/live', new URL(base, window.location.origin)).toString()
  } catch {
    return '/hubs/live'
  }
}

function build(): HubConnection {
  return (
    new HubConnectionBuilder()
      .withUrl(hubUrl(), {
        // Read on every (re)connect rather than captured once: an access token is short-lived and
        // a reconnect an hour later must not present the one from the first handshake.
        accessTokenFactory: () => getAccessToken() ?? '',
      })
      // SignalR's own policy: four quick retries then every thirty seconds. A table with bad wifi
      // reconnects on its own, which is the single largest thing this dependency buys.
      .withAutomaticReconnect([0, 2000, 10000, 30000])
      .configureLogging(import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error)
      .build()
  )
}

/**
 * The shared connection, started if it was not already.
 *
 * Every caller must pair this with {@link release}. Callers that only want to *listen* still take
 * a reference — the connection is what delivers to them.
 */
export async function acquire(): Promise<HubConnection> {
  refs += 1

  if (connection === null) {
    connection = build()
  }

  if (connection.state === HubConnectionState.Disconnected) {
    try {
      await connection.start()
    } catch (error) {
      // A hub that will not start costs live updates and nothing else: every screen reads its
      // state over REST first and re-reads on reconnect. So it is not surfaced to the user —
      // a table with no live updates beats a table with an error banner.
      //
      // But it *is* said out loud in development, because silence here is expensive. The first
      // time this failed the cause was a missing dev-proxy line, and the only symptom anywhere
      // was "live updates do not work" — no error, no request, nothing in the console to search
      // for. Degrading quietly is right; degrading invisibly is not.
      if (import.meta.env.DEV) {
        console.warn(
          `[live] hub connection to ${hubUrl()} failed — live updates are off. ` +
            'In dev this is usually the /hubs proxy; in production, CSP connect-src or a route ' +
            'that drops the Upgrade header.',
          error,
        )
      }
    }
  }

  return connection
}

/**
 * How long the connection outlives its last reference.
 *
 * Long enough to cross a navigation, short enough that a backgrounded tab is not holding a socket
 * for any length of time.
 */
const LINGER_MS = 5000

let linger: ReturnType<typeof setTimeout> | null = null

/**
 * Gives up one reference, stopping the connection a few seconds after the last one goes.
 *
 * **The delay is the point.** Every screen renders its own `AppShell`, and the shell is what holds
 * the announcement subscription — so navigating from one page to another unmounts the old shell
 * before mounting the new one, and the reference count touches zero in between. Stopping
 * immediately meant a full handshake on every navigation, and a window during which an
 * announcement would reach nobody.
 *
 * Stopped eventually rather than never: a phone that backgrounded the app should not hold a
 * socket open, and re-acquiring costs one handshake.
 */
export function release(): void {
  refs = Math.max(0, refs - 1)

  if (refs > 0 || connection === null) return

  if (linger !== null) clearTimeout(linger)

  linger = setTimeout(() => {
    linger = null

    // Re-checked rather than captured: a navigation will have taken a new reference by now, and
    // this timer must not stop a connection something is using.
    if (refs > 0 || connection === null) return

    const stopping = connection
    connection = null
    void stopping.stop().catch(() => undefined)
  }, LINGER_MS)
}

/** The current connection, if there is one. Never starts one. */
export function current(): HubConnection | null {
  return connection
}

/** For tests: forget the connection without touching the ref count of a real one. */
export function resetConnectionForTests(): void {
  if (linger !== null) clearTimeout(linger)
  linger = null
  connection = null
  refs = 0
}
